/**
 * Banner shown after a workspace was recovered from an infrastructure failure
 * via a backupless force-pause: restored files predate the chat transcript,
 * and the user must know work after the backup time may be missing.
 *
 * Renders in the dashboard layout's attention area (same top slot and shared
 * AnnouncementBannerView styling as Maintenance/Announcement/Billing banners);
 * the PowerEditor route publishes the project's notice via
 * useRecoveryNoticeStore. Dismissal is per-recovery (localStorage keyed by
 * resumedAt) and the banner self-expires after 7 days.
 */

import { useState, useEffect } from "react";

import { AnnouncementBannerView } from "@/components/announcement-banner";
import { useRecoveryNoticeStore } from "@/stores/recovery-notice-store";

const NOTICE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function dismissKey(projectId: string, resumedAt: string): string {
  return `recovery-notice-dismissed:${projectId}:${resumedAt}`;
}

export default function WorkspaceRecoveryBanner(): JSX.Element | null {
  const projectId = useRecoveryNoticeStore((s) => s.projectId);
  const recoveryNotice = useRecoveryNoticeStore((s) => s.notice);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!projectId || !recoveryNotice) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(
        localStorage.getItem(dismissKey(projectId, recoveryNotice.resumedAt)) === "1",
      );
    } catch {
      setDismissed(false);
    }
  }, [projectId, recoveryNotice]);

  if (!projectId || !recoveryNotice || dismissed) return null;

  const resumedAtMs = Date.parse(recoveryNotice.resumedAt);
  const expired =
    Number.isNaN(resumedAtMs) || Date.now() - resumedAtMs > NOTICE_MAX_AGE_MS;
  if (expired) return null;

  const handleDismiss = (): void => {
    setDismissed(true);
    try {
      localStorage.setItem(dismissKey(projectId, recoveryNotice.resumedAt), "1");
    } catch {
      // localStorage unavailable, dismissal lasts for this render only
    }
  };

  const isTemplateReset = recoveryNotice.restoredFrom === "template";
  const lossUnlikely = recoveryNotice.lossLikely === false;
  const backupPhrase = recoveryNotice.backupAt
    ? `the backup from ${new Date(recoveryNotice.backupAt).toLocaleString()}`
    : "the last available backup";

  // Fresh backup covered everything the conversation describes: reassure
  // instead of warning (manual edits after the backup remain a small unknown).
  if (lossUnlikely && !isTemplateReset) {
    return (
      <AnnouncementBannerView
        severity="info"
        title="Workspace recovered"
        message="Your workspace was restored from a backup taken shortly before the interruption. If you made manual file edits in the last few minutes, give them a quick check."
        dismissible
        onDismiss={handleDismiss}
      />
    );
  }

  return (
    <AnnouncementBannerView
      severity="warning"
      title={
        isTemplateReset
          ? "Workspace reset to its template"
          : "Workspace recovered from backup"
      }
      message={
        isTemplateReset
          ? "After an infrastructure failure, no backup existed for this project yet, so it was restored to its initial template. Check version history for any committed work."
          : `After an infrastructure failure, your files were restored from ${backupPhrase}. Changes made after that time may need to be redone.`
      }
      dismissible
      onDismiss={handleDismiss}
    />
  );
}
